import {
  isEnhancerUploadFileTooLargeError,
  isEnhancerUploadImageDimensionsTooLargeError,
} from '@/ai/image/client/upload-input-file';
import { TimedMediaPreflightError } from '@/ai/media/client/timed-media-preflight';
import { WorkspaceMediaUploadFileTooLargeError } from '@/ai/media/client/upload-media-input-file';
import { isUploadRequestError } from '@/storage/client';
import type { WorkspaceToastCopy } from '../copy';

export type WorkspaceUploadValidationFailureReason =
  | 'invalid-type'
  | 'too-large'
  | 'too-many';

export type WorkspaceUploadErrorContext = {
  mediaKind?: 'video' | 'audio';
};

export class WorkspaceUploadRejectedFileError extends Error {
  readonly reason: WorkspaceUploadValidationFailureReason;
  readonly maxBytes?: number;
  readonly maxCount?: number;

  constructor(
    reason: WorkspaceUploadValidationFailureReason,
    details: { maxBytes?: number; maxCount?: number } = {}
  ) {
    super(`Workspace upload rejected file: ${reason}`);
    this.name = 'WorkspaceUploadRejectedFileError';
    this.reason = reason;
    this.maxBytes = details.maxBytes;
    this.maxCount = details.maxCount;
  }
}

function interpolate(
  template: string,
  values: Record<string, string | number | undefined>
) {
  return Object.entries(values).reduce(
    (result, [key, value]) =>
      value === undefined
        ? result
        : result.replaceAll(`{${key}}`, String(value)),
    template
  );
}

function formatBytes(bytes: number) {
  const megabytes = bytes / (1024 * 1024);
  const value =
    megabytes >= 10
      ? Math.round(megabytes).toString()
      : megabytes.toFixed(1).replace(/\.0$/, '');
  return `${value} MB`;
}

function formatSeconds(seconds: number) {
  return Number.isInteger(seconds)
    ? String(seconds)
    : seconds.toFixed(1).replace(/\.0$/, '');
}

function resolveMediaTypeMessage(
  kind: 'video' | 'audio',
  copy: WorkspaceToastCopy
) {
  return kind === 'video'
    ? copy.toastUnsupportedVideoFormat
    : copy.toastUnsupportedAudioFormat;
}

function resolveMediaTooLargeMessage(params: {
  kind: 'video' | 'audio';
  maxBytes?: number;
  total?: boolean;
  copy: WorkspaceToastCopy;
}) {
  if (params.maxBytes === undefined) return params.copy.toastFileTooLarge;
  const template =
    params.kind === 'video'
      ? params.total
        ? params.copy.toastVideoTotalSizeTooLargeTemplate
        : params.copy.toastVideoTooLargeTemplate
      : params.total
        ? params.copy.toastAudioTotalSizeTooLargeTemplate
        : params.copy.toastAudioTooLargeTemplate;
  return interpolate(template, { max: formatBytes(params.maxBytes) });
}

function resolveTimedMediaPreflightMessage(
  error: TimedMediaPreflightError,
  copy: WorkspaceToastCopy
) {
  const { kind } = error.details;
  switch (error.reason) {
    case 'invalid-type':
      return resolveMediaTypeMessage(kind, copy);
    case 'file-too-large':
      return resolveMediaTooLargeMessage({
        kind,
        maxBytes: error.details.maxBytes,
        copy,
      });
    case 'total-size-too-large':
      return resolveMediaTooLargeMessage({
        kind,
        maxBytes: error.details.maxBytes,
        total: true,
        copy,
      });
    case 'too-many':
      return interpolate(
        kind === 'video'
          ? copy.toastTooManyVideosTemplate
          : copy.toastTooManyAudioFilesTemplate,
        { max: error.details.maxCount }
      );
    case 'duration-too-short':
      return interpolate(
        kind === 'video'
          ? copy.toastVideoDurationTooShortTemplate
          : copy.toastAudioDurationTooShortTemplate,
        {
          min:
            error.details.minSeconds && formatSeconds(error.details.minSeconds),
        }
      );
    case 'duration-too-long':
      return interpolate(
        kind === 'video'
          ? copy.toastVideoDurationTooLongTemplate
          : copy.toastAudioDurationTooLongTemplate,
        {
          max:
            error.details.maxSeconds && formatSeconds(error.details.maxSeconds),
        }
      );
    case 'total-duration-too-long':
      return interpolate(
        kind === 'video'
          ? copy.toastVideoTotalDurationTooLongTemplate
          : copy.toastAudioTotalDurationTooLongTemplate,
        {
          max:
            error.details.maxSeconds && formatSeconds(error.details.maxSeconds),
        }
      );
    case 'resolution-too-low':
      return interpolate(copy.toastVideoResolutionTooLowTemplate, {
        min: error.details.minShortEdge,
      });
    case 'resolution-too-high':
      return interpolate(copy.toastVideoResolutionTooHighTemplate, {
        max: error.details.maxShortEdge,
      });
    case 'metadata-unreadable':
      return kind === 'video'
        ? copy.toastVideoUnreadable
        : copy.toastAudioUnreadable;
  }
}

function resolveRejectedFileMessage(
  error: WorkspaceUploadRejectedFileError,
  copy: WorkspaceToastCopy,
  context: WorkspaceUploadErrorContext
) {
  if (error.reason === 'invalid-type') {
    return context.mediaKind
      ? resolveMediaTypeMessage(context.mediaKind, copy)
      : copy.toastUnsupportedFileType;
  }
  if (error.reason === 'too-large') {
    if (context.mediaKind) {
      return resolveMediaTooLargeMessage({
        kind: context.mediaKind,
        maxBytes: error.maxBytes,
        copy,
      });
    }
    return error.maxBytes === undefined
      ? copy.toastFileTooLarge
      : interpolate(copy.toastImageTooLargeTemplate, {
          max: formatBytes(error.maxBytes),
        });
  }
  if (error.reason === 'too-many') {
    if (context.mediaKind && error.maxCount !== undefined) {
      return interpolate(
        context.mediaKind === 'video'
          ? copy.toastTooManyVideosTemplate
          : copy.toastTooManyAudioFilesTemplate,
        { max: error.maxCount }
      );
    }
    return copy.toastTooManyFiles;
  }
  return copy.toastUploadFailed;
}

export function resolveWorkspaceUploadFailureMessage(
  error: unknown,
  copy: WorkspaceToastCopy,
  context: WorkspaceUploadErrorContext = {}
) {
  if (error instanceof TimedMediaPreflightError) {
    return resolveTimedMediaPreflightMessage(error, copy);
  }

  if (error instanceof WorkspaceUploadRejectedFileError) {
    return resolveRejectedFileMessage(error, copy, context);
  }

  if (error instanceof WorkspaceMediaUploadFileTooLargeError) {
    return context.mediaKind
      ? resolveMediaTooLargeMessage({
          kind: context.mediaKind,
          maxBytes: error.maxBytes,
          copy,
        })
      : copy.toastFileTooLarge;
  }

  if (isEnhancerUploadFileTooLargeError(error)) {
    return interpolate(copy.toastImageTooLargeTemplate, {
      max: formatBytes(error.maxBytes),
    });
  }

  if (isEnhancerUploadImageDimensionsTooLargeError(error)) {
    return copy.toastImageDimensionsTooLarge;
  }

  if (isUploadRequestError(error)) {
    if (error.status === 408) return copy.toastUploadTimedOut;
    if (error.status === 413) return copy.toastFileTooLarge;
    if (error.status === 429 || error.status >= 500) {
      return copy.toastUploadUnavailable;
    }
  }

  if (
    (error instanceof TypeError &&
      /fetch|network|load failed|failed to load/i.test(error.message)) ||
    (error instanceof DOMException && error.name === 'NetworkError')
  ) {
    return copy.toastUploadNetworkError;
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return copy.toastUploadTimedOut;
  }

  return copy.toastUploadFailed;
}
